// src/components/visits/VisitsTable.jsx
import React from 'react';
import {
  Eye,
  CheckCircle2,
  XCircle,
  Loader2,
  MapPin,
  Image as ImageIcon,
  Video,
} from 'lucide-react';
import { useTheme } from '../../theme/ThemeContext';

const badgeBase =
  'inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-medium';

const statusBadgeClass = (status) => {
  if (!status)
    return 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300';
  switch (status) {
    case 'captured':
    case 'uploading':
      return 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300';
    case 'uploaded':
      return 'bg-sky-50 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300';
    case 'delivered':
      return 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300';
    default:
      return 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300';
  }
};

const approveBadgeClass = (status) => {
  switch (status) {
    case 'pending':
      return 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300';
    case 'approved':
      return 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300';
    case 'rejected':
      return 'bg-rose-50 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300';
    default:
      return 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300';
  }
};

const runTypeBadgeClass = (runType) => {
  if (runType === 'manual') {
    return 'bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300';
  }
  if (runType === 'auto') {
    return 'bg-sky-50 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300';
  }
  return 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300';
};

/**
 * Parse MySQL DATETIME string (e.g. "2025-11-26 04:24:54")
 * as UTC and return Date.
 */
const parseUtcSqlToIstDate = (value) => {
  if (!value) return null;

  const s = String(value).trim();
  if (!s) return null;

  const hasTZ = /[zZ]|[+\-]\d{2}:?\d{2}$/.test(s);
  let utcDate;

  if (hasTZ) {
    utcDate = new Date(s);
    if (Number.isNaN(utcDate.getTime())) return null;
  } else {
    const [datePart, timePartRaw = '00:00:00'] = s.split(/[ T]/);
    const [year, month, day] = datePart.split('-').map(Number);
    const [hour = 0, minute = 0, second = 0] = timePartRaw
      .split(':')
      .map((n) => Number(n || 0));

    if (!year || !month || !day) return null;

    const utcMs = Date.UTC(year, month - 1, day, hour, minute, second);
    utcDate = new Date(utcMs);
  }

  return utcDate;
};

// Format date as IST dd/mm/yyyy
const formatISTDate = (value) => {
  const d = parseUtcSqlToIstDate(value);
  if (!d) return '-';

  return d.toLocaleDateString('en-IN', {
    timeZone: 'Asia/Kolkata',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

// Format time as IST hh:mm
const formatISTTime = (value) => {
  const d = parseUtcSqlToIstDate(value);
  if (!d) return '--:--';

  return d.toLocaleTimeString('en-IN', {
    timeZone: 'Asia/Kolkata',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const VisitsTable = ({
  visits,
  loading,
  error,
  pagination,
  onPageChange,
  onView,
  onApprove,
  onReject,
  approvingId,
}) => {
  const { theme } = useTheme();

  const handlePrev = () => {
    if (pagination.page > 1) onPageChange(pagination.page - 1);
  };

  const handleNext = () => {
    if (pagination.page < pagination.totalPages) {
      onPageChange(pagination.page + 1);
    }
  };

  // per page size for serial numbering (fallback to visits length)
  const perPage = pagination?.limit || visits.length || 1;

  return (
    <section
      className={`rounded-xl border border-slate-200 bg-white/80 shadow-sm dark:border-slate-700 dark:bg-slate-900/80 ${
        theme.card
      }`}
    >
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-800">
          <thead className="bg-slate-50/80 dark:bg-slate-900/70">
            <tr>
              {/* ⭐ S.No header */}
              <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                S.No
              </th>
              <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                Date (IST)
              </th>
              <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                Client / Property
              </th>
              <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                Guard
              </th>
              <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                Run
              </th>
              {/* ⭐ Mode header */}
              <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                Mode
              </th>
              <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                Status
              </th>
              <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                Approval
              </th>
              <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                Media
              </th>
              <th className="px-4 py-2 text-right text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 text-sm dark:divide-slate-800">
            {loading && (
              <tr>
                <td
                  colSpan={10}
                  className="px-4 py-6 text-center text-sm text-slate-500 dark:text-slate-400"
                >
                  <Loader2 className="mr-2 inline-block h-4 w-4 animate-spin" />
                  Loading visits...
                </td>
              </tr>
            )}

            {!loading && error && (
              <tr>
                <td
                  colSpan={10}
                  className="px-4 py-6 text-center text-sm text-rose-500"
                >
                  {error}
                </td>
              </tr>
            )}

            {!loading && !error && visits.length === 0 && (
              <tr>
                <td
                  colSpan={10}
                  className="px-4 py-6 text-center text-sm text-slate-500 dark:text-slate-400"
                >
                  No visits found.
                </td>
              </tr>
            )}

            {!loading &&
              !error &&
              visits.map((visit, index) => {
                const serial =
                  (pagination.page - 1) * perPage + index + 1;

                return (
                  <tr
                    key={visit.visit_id}
                    className="hover:bg-slate-50/70 dark:hover:bg-slate-900/40"
                  >
                    {/* ⭐ Serial Number */}
                    <td className="whitespace-nowrap px-4 py-3 text-xs text-slate-600 dark:text-slate-300">
                      {serial}
                    </td>

                    {/* Date/time */}
                    <td className="whitespace-nowrap px-4 py-3 text-xs text-slate-600 dark:text-slate-300">
                      <div className="font-medium">
                        {formatISTDate(visit.started_at)}
                      </div>
                      <div className="text-[11px] text-slate-400">
                        {formatISTTime(visit.started_at)}
                      </div>
                    </td>

                    {/* Client / property */}
                    <td className="px-4 py-3 text-xs text-slate-700 dark:text-slate-200">
                      <div className="font-medium">{visit.client_name || '-'}</div>
                      <div className="mt-0.5 flex items-center text-[11px] text-slate-500 dark:text-slate-400">
                        <MapPin className="mr-1 h-3 w-3" />
                        <span>{visit.property_name || '-'}</span>
                      </div>
                    </td>

                    {/* Guard */}
                    <td className="px-4 py-3 text-xs text-slate-700 dark:text-slate-200">
                      <div className="font-medium">{visit.guard_name || '-'}</div>
                      <div className="text-[11px] text-slate-400">Guard</div>
                    </td>

                    {/* Run */}
                    <td className="px-4 py-3 text-xs text-slate-700 dark:text-slate-200">
                      <div className="font-medium truncate max-w-[160px]">
                        {visit.run_title || '-'}
                      </div>
                    </td>

                    {/* ⭐ Mode */}
                    <td className="px-4 py-3 text-xs text-slate-700 dark:text-slate-200">
                      <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-0.5 text-[11px] font-medium text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                        {visit.run_type || '-'}
                      </span>
                    </td>

                    {/* Status */}
                    <td className="px-4 py-3 text-xs">
                      <span
                        className={`${badgeBase} ${statusBadgeClass(
                          visit.status
                        )}`}
                      >
                        {visit.status || '-'}
                      </span>
                    </td>

                    {/* Approval */}
                    <td className="px-4 py-3 text-xs">
                      <span
                        className={`${badgeBase} ${approveBadgeClass(
                          visit.approve_status
                        )}`}
                      >
                        {visit.approve_status || '-'}
                      </span>
                    </td>

                    {/* Media counts */}
                    <td className="px-4 py-3 text-xs text-slate-700 dark:text-slate-200">
                      <div className="flex items-center gap-2">
                        <span className="inline-flex items-center gap-1 text-[11px]">
                          <ImageIcon className="h-3 w-3 text-slate-400" />
                          {visit.photo_count || 0}
                        </span>
                        <span className="inline-flex items-center gap-1 text-[11px]">
                          <Video className="h-3 w-3 text-slate-400" />
                          {visit.video_count || 0}
                        </span>
                      </div>
                    </td>

                    {/* Actions */}
                    <td className="px-4 py-3 text-right text-xs">
                      <div className="flex justify-end gap-2">
                        {/* View */}
                        <button
                          type="button"
                          onClick={() => onView(visit)}
                          className="inline-flex items-center rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-medium text-slate-700 shadow-sm hover:border-sky-300 hover:text-sky-600 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:border-sky-500 dark:hover:text-sky-400"
                        >
                          <Eye className="mr-1 h-3.5 w-3.5" />
                          View
                        </button>

                        {/* Approve / Reject only for pending manual visits */}
                        {visit.run_type === 'manual' &&
                          visit.approve_status === 'pending' && (
                            <>
                              <button
                                type="button"
                                disabled={approvingId === visit.visit_id}
                                onClick={() => onApprove(visit)}
                                className="inline-flex items-center rounded-full bg-emerald-500 px-2.5 py-1 text-[11px] font-medium text-white shadow-sm hover:bg-emerald-600 disabled:cursor-not-allowed disabled:opacity-60"
                              >
                                {approvingId === visit.visit_id ? (
                                  <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                                ) : (
                                  <CheckCircle2 className="mr-1 h-3.5 w-3.5" />
                                )}
                                Approve
                              </button>

                              <button
                                type="button"
                                disabled={approvingId === visit.visit_id}
                                onClick={() => onReject(visit)}
                                className="inline-flex items-center rounded-full bg-rose-500 px-2.5 py-1 text-[11px] font-medium text-white shadow-sm hover:bg-rose-600 disabled:cursor-not-allowed disabled:opacity-60"
                              >
                                {approvingId === visit.visit_id ? (
                                  <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                                ) : (
                                  <XCircle className="mr-1 h-3.5 w-3.5" />
                                )}
                                Reject
                              </button>
                            </>
                          )}
                      </div>
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between border-t border-slate-100 px-4 py-3 text-xs text-slate-500 dark:border-slate-800 dark:text-slate-400">
        <div>
          Page <strong>{pagination.page}</strong> of{' '}
          <strong>{pagination.totalPages}</strong> • Total{' '}
          <strong>{pagination.total}</strong> visits
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handlePrev}
            disabled={pagination.page <= 1}
            className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-medium text-slate-600 shadow-sm disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900"
          >
            Prev
          </button>
          <button
            type="button"
            onClick={handleNext}
            disabled={pagination.page >= pagination.totalPages}
            className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-medium text-slate-600 shadow-sm disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900"
          >
            Next
          </button>
        </div>
      </div>
    </section>
  );
};

export default VisitsTable;
